<template>
	<q-card class="card-main bg-black text-white">
		<q-toolbar>
			<q-toolbar-title></q-toolbar-title>

			<q-btn flat round dense icon="fal fa-times" v-close-popup />
		</q-toolbar>

		<q-separator />

		<q-card-section class="q-pa-md">
			<div class="row q-col-gutter-lg">
				<div class="col-6">
					<q-img
						v-if="getImage !== ''"
						:src="getImage"
						@error="getImageError"
						class="rounded-borders responsive"
					/>
				</div>
				<div class="col-6">
					{{ library.title }}
				</div>

				<div class="col-sm-12 col-lg-9">
					<div class="row full-height q-col-gutter-sm">
						<div
							:style="moreDescription ? '' : 'height: 100px; overflow: hidden !important'"
							class="col-12 description"
						>
							<span ref="libraryDescriptionText" v-html="library.description" />
						</div>
					</div>
					<span
						v-if="showButtonMoreText"
						class="cursor-pointer text-white text-weight-bold"
						@click="setSize"
						>{{ $t('see_more') }}</span
					>
				</div>
			</div>
		</q-card-section>

		<q-card-section :class="getPlayerClass">
			<pdf-visualizer
				:file_preview="library.file_preview"
				v-if="['pdf'].includes(library.extension)"
			/>

			<office-visualizer
				origin="library"
				:file_url="library.file_url_complete"
				v-else-if="['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(library.extension)"
			/>

			<video-visualizer
				:file_url="library.file_url_complete"
				:id="library.id"
				v-else-if="['mp4', 'mkv', 'mov', 'wmv'].includes(library.extension)"
			/>

			<audio-visualizer
				:file_url="library.file_url_complete"
				:id="library.id"
				v-else-if="['mp3', 'ogg', 'wav'].includes(library.extension)"
			/>

			<image-visualizer
				:file_url="library.file_url_complete"
				v-else-if="['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(library.extension)"
			/>
		</q-card-section>

		<div class="row bg-grey-10 q-pa-md fixed-bottom">
			<div class="col-12 text-grey-7">
				<q-btn
					rounded
					:color="library.allow_download ? 'positive' : 'grey-5'"
					:label="$t('download')"
					class="q-px-md q-mr-md"
					:disable="!library.allow_download"
					@click="downloadFile()"
				/>
				<q-icon :name="$extensionIcon(library.extension).icon" color="grey-5" class="q-mr-sm" />
				<span class="text-uppercase text-grey-7"> {{ library.extension }}</span>
				<q-icon name="fal fa-hdd" class="q-ml-md q-mr-sm" color="grey-5" />
				<span class="text-grey-7"> {{ library.size }}</span>

				<template v-if="library.allow_download">
					<q-icon color="grey-5" name="fal fa-download" class="q-ml-md q-mr-sm" />
					<span class="text-grey-7"> {{ library.hits }}</span>
				</template>
			</div>
		</div>
	</q-card>
</template>

<script>
import Pdf from 'components/visualizers/Pdf'
import Office from 'components/visualizers/Office'
import Video from 'components/visualizers/Video'
import Audio from 'components/visualizers/Audio'
import Image from 'components/visualizers/Image'
import { mapGetters } from 'vuex'

export default {
	name: 'LibraryVisualization',

	props: ['library'],

	data() {
		return {
			moreDescription: false,
			showButtonMoreText: false,
		}
	},

	components: {
		'pdf-visualizer': Pdf,
		'office-visualizer': Office,
		'video-visualizer': Video,
		'audio-visualizer': Audio,
		'image-visualizer': Image,
	},

	computed: {
		...mapGetters({
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
		}),

		getImage() {
			const image = this.library.image_urls.find((e) => e.name === 'original')
			if (image) return image.image_url
			else {
				let obj = this.clients.find((obj) => obj.id == this.id_user_client_selected)
				if (obj) return obj.group_library_file_placeholder
				else return ''
			}
		},

		getPlayerClass() {
			if (['mp4', 'mkv', 'mov', 'wmv'].includes(this.library.extension)) return 'card-player-video'
			else if (['pdf'].includes(this.library.extension)) return 'card-player-pdf'
			else if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(this.library.extension))
				return 'card-player-office'
			else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(this.library.extension))
				return 'card-player-image'
		},
	},

	methods: {
		getImageError(event) {
			let obj = this.clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				if (
					obj.group_library_file_placeholder !== 'https://image.jpg' &&
					obj.group_library_file_placeholder !== ''
				)
					event.target.src = obj.group_library_file_placeholder
				else event.target.src = 'placeholders/file.png'
			} else event.target.src = 'placeholders/file.png'
		},

		downloadFile() {
			let file = this.library.file_url_complete.substring(
				this.library.file_url_complete.lastIndexOf('/') + 1
			)
			let extension = file.substring(file.lastIndexOf('.'))
			let fileName = `${this.library.title}${extension}`

			if (this.$q.platform.is.cordova && this.$q.platform.is.ios) {
				window.open(this.library.file_url_download, '_system')
				this.hitDownload()
			} else if (this.$q.platform.is.cordova && this.$q.platform.is.android) {
				let uriString = this.library.file_url_download
				let notificationTitle = `${this.$t('downloading')} ${this.library.title}`

				window.resolveLocalFileSystemURL(cordova.file.externalRootDirectory, (rootDirectory) => {
					rootDirectory.getDirectory('download', { create: true }, (downloadDirectory) => {
						downloadDirectory.getDirectory(
							process.env.PROJECT_FOLDER,
							{ create: true },
							(projectDirectory) => {
								projectDirectory.getFile(fileName, { create: true }, (targetFile) => {
									let downloader = new BackgroundTransfer.BackgroundDownloader()
									let download = downloader.createDownload(uriString, targetFile, notificationTitle)

									this.$q.notify({
										message: this.$t('file_download_started'),
										type: 'info',
										icon: 'fal fa-download',
									})

									this.hitDownload()

									download.startAsync().then(
										(e) => {
											this.$q.notify({
												message: this.$t('file_download_finished'),
												type: 'positive',
												icon: 'fal fa-download',
											})
										},
										(e) => {
											this.$q.notify({
												message: this.$t('file_download_failed'),
												type: 'negative',
												icon: 'fal fa-download',
											})
										},
										(e) => {
											console.log(e)
										}
									)
								})
							}
						)
					})
				})
			} else {
				window.open(this.library.file_url_download, '_blank')
				this.hitDownload()
			}
		},

		hitDownload() {
			this.$axios
				.get(`/etraining_library_files/files/${this.library.id}/request_file_download/`)
				.then((response) => {
					this.$emit('changeHits', this.library.id)
				})
				.catch((e) => {
					console.log(e)
				})
		},

		setSize() {
			this.moreDescription = !this.moreDescription
		},
	},
	mounted() {
		setTimeout(() => {
			this.showButtonMoreText =
				this.$refs.libraryDescriptionText.getBoundingClientRect().height > 100 ? true : false
		}, 200)
	},
}
</script>

<style lang="scss" scoped>
.description {
	line-height: 1.5rem;
}
.card-main {
	margin-bottom: 60px;
	@media (min-width: $breakpoint-sm-min) {
		min-width: 70vw;
		width: 70vw;
		height: 85vh;
	}
	@media (min-width: $breakpoint-lg-min) {
		min-width: 55vw;
		width: 55vw;
		height: 80vh;
	}
}
.card-player-video {
	margin: 0 auto;
	margin-bottom: 60px;
	@media (min-width: $breakpoint-sm-min) {
		width: 70%;
	}
	@media (min-width: $breakpoint-lg-min) {
		width: 85%;
	}
}
.card-player-pdf {
	margin: 0 auto;
	margin-bottom: 60px;
	@media (min-width: $breakpoint-sm-min) {
		width: 60%;
	}
	@media (min-width: $breakpoint-lg-min) {
		width: 75%;
	}
}
.card-player-office {
	margin: 0 auto;
	margin-bottom: 60px;
	@media (min-width: $breakpoint-sm-min) {
		width: 60%;
	}
	@media (min-width: $breakpoint-lg-min) {
		width: 75%;
	}
}
.card-player-image {
	margin: 0 auto;
	margin-bottom: 60px;
	@media (min-width: $breakpoint-sm-min) {
		width: 60%;
	}
	@media (min-width: $breakpoint-lg-min) {
		width: 70%;
	}
}
</style>
